/**
 * Created by pyt on 2017-03-07.
 */
import Exception from '@/common/models/Exception';
import FilterBase from '@/common/models/query-params/FilterBase';
import { isBoolean } from '@/globals';

import * as ComparisonOperator from './FilterComparisonOperator';
import LogicalOperator from './FilterLogicalOperator.enum';

type FilterValue = string | number | boolean;

export default class Filter extends FilterBase {
  type: string;
  property: string;
  values: Array<FilterValue>;
  comparisonOperator: string;
  logicalOperator: string;
  label: string;
  customQueryParams: (values: Array<string | number | boolean>, comparisonOperator: string) => string;
  normalizedComparisonOperator: string;
  instance: string;

  constructor(
    property = null,
    values: Array<string | number | boolean>,
    comparisonOperator = ComparisonOperator.EQUAL,
    logicalOperator = LogicalOperator.OR,
    label = null,
    customQueryParams = null,
  ) {
    super();
    this.type = 'Filter';
    this.property = property;
    this.values = values;
    this.comparisonOperator = comparisonOperator;
    this.logicalOperator = logicalOperator;
    this.label = label;
    this.customQueryParams = customQueryParams;

    // Validate
    if (this.values?.length > 1 && this.logicalOperator === LogicalOperator.NOT) {
      throw new Exception('You cannot use NOT when multiple values are supplied.');
    }
  }

  get isEmpty() {
    return this.values.length === 0;
  }

  get value() {
    return this.values.length > 0 ? this.values[0] : null;
  }

  set value(val) {
    if (this.values.length > 0) {
      this.values[0] = val;
    } else {
      this.values.push(val);
    }
  }

  toQueryParam() {
    let queryString = '';
    const escapedValues = (this.values || []).map((x) => (typeof x === 'string' ? x.replace("'", "''") : x));
    if (this.property === 'package_children') {
      // Special case filtering
      queryString += this.customQueryParams(this.values, this.comparisonOperator);
    } else if (
      this.comparisonOperator !== ComparisonOperator.IN &&
      this.comparisonOperator !== ComparisonOperator.NOT_IN
    ) {
      escapedValues.forEach((value) => {
        if (queryString !== '') {
          queryString += ` ${this.logicalOperator} `;
        }
        if (
          queryString === '' &&
          [ComparisonOperator.EQUAL, ComparisonOperator.NOT_EQUAL].includes(this.comparisonOperator) &&
          escapedValues.length > 1
        ) {
          queryString += '(';
        }
        if (this.logicalOperator === LogicalOperator.NOT) {
          queryString += `${LogicalOperator.NOT} `;
        }

        const quoteValue = value !== null && !isBoolean(value);
        switch (this.comparisonOperator) {
          case ComparisonOperator.LIKE:
            if (`${value}`.indexOf('%') === -1) {
              value = `%${value}%`;
            }
            break;
          case ComparisonOperator.STARTS_WITH:
            if (`${value}`.indexOf('%') === -1) {
              value = `${value}%`;
              this.normalizedComparisonOperator = ComparisonOperator.LIKE;
            }
            break;
          case ComparisonOperator.ENDS_WITH:
            if (`${value}`.indexOf('%') === -1) {
              value = `%${value}`;
              this.normalizedComparisonOperator = ComparisonOperator.LIKE;
            }
            break;
          default:
            break;
        }
        if (quoteValue) {
          queryString += `${this.property} ${Filter.getNormalizedComparisonOperator(this.comparisonOperator)} '${value}'`;
        } else {
          queryString += `${this.property} ${Filter.getNormalizedComparisonOperator(this.comparisonOperator)} ${value}`;
        }
      });
      if (
        [ComparisonOperator.EQUAL, ComparisonOperator.NOT_EQUAL].includes(this.comparisonOperator) &&
        escapedValues.length > 1
      ) {
        queryString += ')';
      }
    } else {
      if (queryString !== '') {
        queryString += ` ${this.logicalOperator} `;
      }
      if (this.logicalOperator === LogicalOperator.NOT) {
        queryString += `${LogicalOperator.NOT} `;
      }
      if (escapedValues.every((x: FilterValue) => typeof x === 'number')) {
        queryString += `${this.property} ${Filter.getNormalizedComparisonOperator(this.comparisonOperator)} (${escapedValues.join(',')})`;
      } else if (this.property === 'unit_languages') {
        // Special case filtering
        queryString += `unit_languages.any(ul:ul.language_id ${Filter.getNormalizedComparisonOperator(this.comparisonOperator)} (${this.values.join(',')})`;
      } else if (this.customQueryParams != null) {
        queryString += this.customQueryParams(this.values, this.comparisonOperator);
      } else {
        queryString += `${this.property} ${Filter.getNormalizedComparisonOperator(this.comparisonOperator)} (${escapedValues
          .map((x) => `'${x}'`)
          .join(',')})`;
      }
    }
    return queryString;
  }

  /**
   * Generates a combined filtering function based on an array of filters where each filter must be met.
   * @param {Filter[]} filters - An array of filter objects.
   * @returns {function} A combined filtering function.
   */
  static generateCombinedFilterFunction(filters) {
    return (objectToFilter) => filters.every((filter) => filter.matches(objectToFilter));
  }

  /**
   * Generates a combined filtering function based on an array of filters where at least one filter is met.
   * @param {Filter[]} filters - An array of filter objects.
   * @returns {function} A combined filtering function.
   */
  static generateOrFilterFunction(filters) {
    return (objectToFilter) => filters.some((filter) => filter.matches(objectToFilter));
  }

  /**
   * Checks if the filter matches the object based on its properties.
   * @param {object} objectToFilter - The object to filter.
   * @returns {boolean} True if the object matches the filter, otherwise false.
   */
  matches(objectToFilter) {
    if (!this.property || this.values.length === 0) {
      return true;
    }

    const objectValue = objectToFilter[this.property];

    if (this.logicalOperator === LogicalOperator.NOT) {
      return !this.values.includes(objectValue);
    }
    const lowercaseObjectValue = `${objectValue}`.toLowerCase();
    const lowercaseValues = this.values.map((value) => `${value}`.toLowerCase());

    switch (this.comparisonOperator) {
      case ComparisonOperator.EQUAL:
        return lowercaseValues.includes(lowercaseObjectValue);
      case ComparisonOperator.NOT_EQUAL:
        return !lowercaseValues.includes(lowercaseObjectValue);
      case ComparisonOperator.GREATER_THAN:
        return lowercaseObjectValue > lowercaseValues[0];
      case ComparisonOperator.GREATER_THAN_OR_EQUAL:
        return lowercaseObjectValue >= lowercaseValues[0];
      case ComparisonOperator.LESS_THAN:
        return lowercaseObjectValue < lowercaseValues[0];
      case ComparisonOperator.LESS_THAN_OR_EQUAL:
        return lowercaseObjectValue <= lowercaseValues[0];
      case ComparisonOperator.LIKE:
        return lowercaseValues.some((value) => lowercaseObjectValue.includes(value));
      case ComparisonOperator.IN:
        return lowercaseValues.includes(lowercaseObjectValue);
      case ComparisonOperator.NOT_IN:
        return !lowercaseValues.includes(lowercaseObjectValue);
      case ComparisonOperator.STARTS_WITH:
        return typeof lowercaseObjectValue === 'string' && lowercaseObjectValue.startsWith(lowercaseValues[0]);
      case ComparisonOperator.ENDS_WITH:
        return typeof lowercaseObjectValue === 'string' && lowercaseObjectValue.endsWith(lowercaseValues[0]);
      default:
        return false; // Invalid operator
    }
  }

  static getNormalizedComparisonOperator(operator) {
    switch (operator) {
      case ComparisonOperator.STARTS_WITH:
      case ComparisonOperator.ENDS_WITH:
        return ComparisonOperator.LIKE;
      default:
        return operator;
    }
  }

  clear() {
    this.comparisonOperator = ComparisonOperator.LIKE;
    this.values = [];
  }

  serializeForQueryString() {
    return `${this.property}~${this.comparisonOperator}~${this.values}`;
  }

  static deserializeFromQueryString(filter, serialized) {
    const fragments = serialized.split('~');
    const values = fragments
      .slice(2)
      .map((x) => x?.split(','))
      .flat();
    const instance = new Filter(
      filter.property,
      values,
      fragments[1],
      LogicalOperator.OR,
      filter.label,
      filter.customQueryParams,
    );
    return instance;
  }

  toApi() {
    return {
      property: this.property,
      values: this.values,
      comparison_operator: this.comparisonOperator,
      logical_operator: this.logicalOperator,
      label: this.label,
      custom_query_params: this.customQueryParams,
    };
  }

  static fromApi(apiObject) {
    const filter = new Filter(
      apiObject.property,
      apiObject.values,
      apiObject.comparison_operator,
      apiObject.logical_operator,
      apiObject.label,
      apiObject.custom_query_params,
    );
    filter.instance = apiObject.instance;
    return filter;
  }
}
